import { FC } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { Trim, TrimRankingQuestion } from '../../types';
import { formatPricing } from '../../utils/pricing';
import { getItemSpecifications } from './wizard-utils';
import { SpecField } from './spec-field';
import type { TrimRankingSelection } from './types';
import { useQuestionnaireResponse } from './questionnaire-response-context';

interface TrimRankingWizardProps {
  question: TrimRankingQuestion;
}

/**
 * Renders the interactive body for a TrimRanking question.
 * Users can add trims from a list, re-order them, set weights, and configure
 * per-trim specification values.
 * @param root0
 * @param root0.question
 */
export const TrimRankingWizard: FC<TrimRankingWizardProps> = ({ question }) => {
  const {
    catalog,
    answers,
    itemSpecValues,
    handleAnswerChange,
    handleSpecChange,
  } = useQuestionnaireResponse();
  const answer = (answers[question.id] as TrimRankingSelection[]) ?? [];
  const specValues = itemSpecValues[question.id] ?? {};

  const onAnswerChange = (value: TrimRankingSelection[]) =>
    handleAnswerChange(question.id, value);
  const onSpecChange = (selectionId: string, specId: string, v: string) =>
    handleSpecChange(question.id, selectionId, specId, v);

  const availableTrims = question.availableTrimIds
    .map((id) => catalog.trim.find((t) => t.id === id))
    .filter(Boolean) as Trim[];

  const handleTrimSelect = (trimId: string) => {
    const trim = availableTrims.find((t) => t.id === trimId);
    const newSelection: TrimRankingSelection = {
      id: crypto.randomUUID(),
      trimId,
      rank: answer.length + 1,
      weight: trim?.minQty ?? 1,
    };
    onAnswerChange([...answer, newSelection]);
  };

  const handleTrimRemove = (selectionId: string) => {
    const remaining = answer.filter((s) => s.id !== selectionId);
    onAnswerChange(remaining.map((s, i) => ({ ...s, rank: i + 1 })));
  };

  const handleMoveUp = (selectionId: string) => {
    const idx = answer.findIndex((s) => s.id === selectionId);
    if (idx <= 0) return;
    const next = [...answer];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onAnswerChange(next.map((s, i) => ({ ...s, rank: i + 1 })));
  };

  const handleMoveDown = (selectionId: string) => {
    const idx = answer.findIndex((s) => s.id === selectionId);
    if (idx >= answer.length - 1) return;
    const next = [...answer];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onAnswerChange(next.map((s, i) => ({ ...s, rank: i + 1 })));
  };

  const handleWeightChange = (
    selectionId: string,
    weight: number | undefined,
  ) => {
    onAnswerChange(
      answer.map((s) => (s.id === selectionId ? { ...s, weight } : s)),
    );
  };

  // Compute max spec count once to keep the table columns consistent
  const maxSpecs = answer.reduce((max, sel) => {
    const trim = availableTrims.find((t) => t.id === sel.trimId);
    return trim
      ? Math.max(max, getItemSpecifications(catalog, trim.id).length)
      : max;
  }, 0);

  return (
    <Stack spacing={3}>
      {/* Ranked selections table */}
      {answer.length > 0 && (
        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Trim</TableCell>
                  {maxSpecs > 0 && (
                    <TableCell colSpan={maxSpecs}>Options</TableCell>
                  )}
                  <TableCell align="right">Weight</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...answer]
                  .sort((a, b) => a.rank - b.rank)
                  .map((selection) => {
                    const trim = availableTrims.find(
                      (t) => t.id === selection.trimId,
                    );
                    if (!trim) return null;

                    const trimSpecs = getItemSpecifications(catalog, trim.id);

                    return (
                      <TableRow key={selection.id}>
                        {/* Rank + move buttons */}
                        <TableCell component="th" scope="row">
                          <Stack
                            direction="row"
                            spacing={0}
                            alignItems="center"
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ minWidth: 20 }}
                            >
                              {selection.rank}
                            </Typography>
                            <Stack direction="column" spacing={0}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveUp(selection.id)}
                                disabled={selection.rank === 1}
                                sx={{ minWidth: 'auto', px: 0.5, py: 0.25 }}
                              >
                                <KeyboardArrowUp fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveDown(selection.id)}
                                disabled={selection.rank === answer.length}
                                sx={{ minWidth: 'auto', px: 0.5, py: 0.25 }}
                              >
                                <KeyboardArrowDown fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </TableCell>

                        {/* Trim name */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {trim.title}
                          </Typography>
                        </TableCell>

                        {/* Spec columns */}
                        {Array.from({ length: maxSpecs }, (_, specIdx) => {
                          const spec = trimSpecs[specIdx];
                          if (!spec)
                            return <TableCell key={specIdx}>-</TableCell>;

                          const selValues = specValues[selection.id] || {};
                          const currentValue =
                            selValues[spec.id] || spec.defaultValue || '';

                          return (
                            <TableCell key={specIdx}>
                              <SpecField
                                spec={spec}
                                value={currentValue}
                                onChange={(v) =>
                                  onSpecChange(selection.id, spec.id, v)
                                }
                                compact
                              />
                            </TableCell>
                          );
                        })}

                        {/* Weight (lbs) — shows error if below trim's minQty */}
                        <TableCell align="right">
                          {(() => {
                            const min = trim.minQty ?? 0.1;
                            const isEmpty = selection.weight === undefined;
                            const belowMin =
                              !isEmpty && selection.weight! < min;
                            const hasError = isEmpty || belowMin;
                            return (
                              <TextField
                                type="number"
                                size="small"
                                value={isEmpty ? '' : selection.weight}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  handleWeightChange(
                                    selection.id,
                                    raw === '' ? undefined : parseFloat(raw),
                                  );
                                }}
                                error={hasError}
                                slotProps={{
                                  input: { inputProps: { step: 0.1 } },
                                }}
                                helperText={
                                  isEmpty
                                    ? 'Required'
                                    : belowMin
                                      ? `Min ${min} lbs`
                                      : trim.minQty
                                        ? `Min ${trim.minQty} lbs`
                                        : undefined
                                }
                                sx={{ width: 90 }}
                              />
                            );
                          })()}
                        </TableCell>

                        {/* Remove */}
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleTrimRemove(selection.id)}
                          >
                            <Close />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Available trims to add */}
      <Box>
        <Stack spacing={2}>
          {availableTrims.map((trim) => (
            <Card
              key={trim.id}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderColor: 'divider',
                borderWidth: 2,
                outline: '2px solid transparent',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                minHeight: trim.imageUrl ? { xs: 120, md: 160 } : 80,
                '&:hover': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              }}
              onClick={() => handleTrimSelect(trim.id)}
            >
              <Stack
                direction="row"
                sx={{
                  cursor: 'pointer',
                  minHeight: trim.imageUrl ? { xs: 120, md: 160 } : 80,
                  transition: 'min-height 0.3s ease',
                }}
              >
                {trim.imageUrl && (
                  <Box
                    sx={{
                      width: { xs: 120, md: 160 },
                      minWidth: { xs: 120, md: 160 },
                      alignSelf: 'stretch',
                      position: 'relative',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={trim.imageUrl}
                      alt={trim.title || ''}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}

                <Stack
                  sx={{ flex: 1, minWidth: 0, p: 2 }}
                  justifyContent="flex-start"
                  spacing={0.5}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="h5">
                      {trim.title || 'Unnamed'}
                    </Typography>
                    <Button size="small" variant="outlined">
                      Add
                    </Button>
                  </Stack>
                  {trim.description && (
                    <Typography variant="body2" color="text.secondary">
                      {trim.description}
                    </Typography>
                  )}
                  {trim.pricing && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontWeight: 'medium' }}
                    >
                      {formatPricing(trim.pricing)}
                    </Typography>
                  )}
                  {trim.minQty && (
                    <Chip
                      label={`Min ${trim.minQty} lbs`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start' }}
                    />
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      {answer.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Select trims to rank in order of preference
        </Typography>
      )}
    </Stack>
  );
};
